/*
 * Helper für die Kommunikation mit der Signatur-API
 * Verwendet den Token aus dem Taskpane-OAuth (auth-helper), kein separater Office-Dialog.
 */

import { showMessage } from "./message-helper";
import { API_BASE_URL } from "../config";
import { getAccessToken as getTokenFromAuth } from "./auth-helper";
import * as $ from "jquery";
import type { AudienceGroup } from "./audience-api";

export interface Signature {
  id: number;
  name: string;
  content: string;
  createdAt: string;
  updatedAt?: string;
  targetAll: boolean;
  targetGroupIds: string[];
  targetGroups: AudienceGroup[];
  /** false = Editor sieht die Signatur, gehört aber nicht zur Zielgruppe (kein „Verwenden“). */
  canUse?: boolean;
  /** Editor-Standardsignatur für neue E-Mails (Unternehmens-Default). */
  isOrgDefaultSignature?: boolean;
  /** Editor-Standardsignatur für Antworten und Weiterleitungen. */
  isOrgReplyForwardDefaultSignature?: boolean;
}

/** Wird geworfen, wenn der Server das Rendern wegen fehlender Entra-Gruppen-Zugehörigkeit ablehnt. */
export class SignatureNotTargetedError extends Error {
  constructor(message?: string) {
    super(message ?? "Signature not targeted at current user");
    this.name = "SignatureNotTargetedError";
  }
}

/**
 * Ruft das Access Token aus dem Taskpane-OAuth ab (Consent im Taskpane).
 * Wirft, wenn kein Token vorhanden – dann soll das Taskpane die Consent-Ansicht anzeigen.
 */
function getAccessToken(): string {
  const token = getTokenFromAuth();
  if (!token) {
    const msg = "Nicht angemeldet. Bitte zuerst im Taskpane anmelden.";
    showMessage(msg);
    throw new Error(msg);
  }
  return token;
}

/**
 * Ruft alle Signaturen vom Backend ab
 */
export async function getAllSignatures(): Promise<Signature[]> {
  try {
    const token = await getAccessToken();
    console.log(`Lade Signaturen von: ${API_BASE_URL}/api/signatures`);
    const response = await $.ajax({
      type: "GET",
      url: `${API_BASE_URL}/api/signatures`,
      contentType: "application/json",
      headers: {
        Authorization: `Bearer ${token}`
      },
      cache: false,
      crossDomain: true,
      xhrFields: {
        withCredentials: false
      }
    });
    console.log("Signaturen erfolgreich geladen:", response);
    return response;
  } catch (err: any) {
    // Korrekte Extraktion des Status-Codes
    let statusCode = "N/A";
    if (typeof err.status === "number") {
      statusCode = err.status.toString();
    } else if (err.statusCode) {
      statusCode = err.statusCode.toString();
    }
    
    const errorMsg = err.responseText || err.responseJSON?.message || err.message || "Unbekannter Fehler";

    // 401: Aufrufer (loadSignatures) erneuert Token still — kein console.error / keine Zwischenmeldung.
    if (statusCode === "401") {
      throw err;
    }

    console.error("Fehler beim Laden der Signaturen:", {
      status: statusCode,
      readyState: err.readyState,
      message: errorMsg,
      url: `${API_BASE_URL}/api/signatures`,
      error: err
    });
    
    // Spezielle Fehlermeldung für Verbindungsprobleme
    if (err.readyState === 0 || statusCode === "0" || statusCode === "N/A") {
      showMessage(`Verbindungsfehler: Kann nicht auf ${API_BASE_URL}/api/signatures zugreifen. Stellen Sie sicher, dass das Backend läuft und CORS aktiviert ist.`);
    } else if (statusCode === "403") {
      showMessage(`Authentifizierungsfehler (Status: ${statusCode}): Bitte melden Sie sich erneut an.`);
    } else {
      showMessage(`Fehler beim Laden der Signaturen (Status: ${statusCode}): ${errorMsg}`);
    }
    throw err;
  }
}

/**
 * Ruft eine Signatur nach ID ab
 */
export async function getSignatureById(id: number): Promise<Signature> {
  try {
    const token = await getAccessToken();
    const response = await $.ajax({
      type: "GET",
      url: `${API_BASE_URL}/api/signatures/${id}`,
      contentType: "application/json",
      headers: {
        Authorization: `Bearer ${token}`
      },
      cache: false,
    });
    return response;
  } catch (err: any) {
    showMessage(`Fehler beim Laden der Signatur: ${err.responseText || err.message}`);
    throw err;
  }
}

/**
 * Erstellt eine neue Signatur
 */
export async function createSignature(signature: {
  name: string;
  content: string;
  targetAll: boolean;
  targetGroupIds: string[];
  isOrgDefaultSignature?: boolean;
  isOrgReplyForwardDefaultSignature?: boolean;
}): Promise<Signature> {
  try {
    const token = await getAccessToken();
    const response = await $.ajax({
      type: "POST",
      url: `${API_BASE_URL}/api/signatures`,
      contentType: "application/json",
      headers: {
        Authorization: `Bearer ${token}`
      },
      data: JSON.stringify(signature),
      cache: false,
    });
    return response;
  } catch (err: any) {
    showMessage(`Fehler beim Erstellen der Signatur: ${err.responseText || err.message}`);
    throw err;
  }
}

/**
 * Aktualisiert eine bestehende Signatur
 */
export async function updateSignature(signature: Signature): Promise<void> {
  try {
    const token = await getAccessToken();
    await $.ajax({
      type: "PUT",
      url: `${API_BASE_URL}/api/signatures/${signature.id}`,
      contentType: "application/json",
      headers: {
        Authorization: `Bearer ${token}`
      },
      data: JSON.stringify(signature),
      cache: false,
    });
  } catch (err: any) {
    showMessage(`Fehler beim Aktualisieren der Signatur: ${err.responseText || err.message}`);
    throw err;
  }
}

/**
 * Löscht eine Signatur
 */
export async function deleteSignature(id: number): Promise<void> {
  try {
    const token = await getAccessToken();
    await $.ajax({
      type: "DELETE",
      url: `${API_BASE_URL}/api/signatures/${id}`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      cache: false,
    });
  } catch (err: any) {
    showMessage(`Fehler beim Löschen der Signatur: ${err.responseText || err.message}`);
    throw err;
  }
}

/**
 * Ruft die gerenderte Signatur ab (Platzhalter wie {{Vorname}} durch User-Daten aus Graph ersetzt).
 * Für „Signatur verwenden“ / In E-Mail einfügen.
 */
export async function getRenderedSignature(id: number): Promise<string> {
  const token = getAccessToken();
  try {
    const response = await $.ajax({
      type: "GET",
      url: `${API_BASE_URL}/api/signatures/${id}/rendered`,
      contentType: "application/json",
      headers: {
        Authorization: `Bearer ${token}`
      },
      cache: false,
      crossDomain: true,
    });
    return response?.content ?? "";
  } catch (err: any) {
    const status = typeof err?.status === "number" ? err.status : undefined;
    if (status === 403 && String(err?.responseText ?? "").includes("not_in_audience")) {
      throw new SignatureNotTargetedError();
    }
    throw err;
  }
}

/** Wird geworfen, wenn die API 403 (consent_required) zurückgibt – User kann dann „Zustimmung erteilen“ auslösen. */
export class ConsentRequiredError extends Error {
  constructor(message?: string) {
    super(message ?? "Consent required");
    this.name = "ConsentRequiredError";
  }
}

/**
 * Ruft die Werte der genannten Graph-Eigenschaften für den angemeldeten Benutzer ab
 * (Token → Wert). Quelle: Microsoft Graph (wenn OBO konfiguriert) oder JWT-Claims als Fallback.
 * Wird für die Vorschau-Ersetzung im Taskpane und die Markierung befüllter Felder genutzt.
 *
 * Die Feldliste kommt aus dem Add-in (utils/graphUserFields.ts) und geht als Body mit –
 * als Query-String wäre sie zu lang. Bei 403 wird ConsentRequiredError geworfen, damit das
 * Taskpane „Zustimmung erteilen“ anbieten kann; sonst bei Fehler: leeres Objekt (kein Crash).
 */
export async function getUserProfileValues(fields: readonly string[]): Promise<Record<string, string>> {
  try {
    const token = await getAccessToken();
    const response = await $.ajax({
      type: "POST",
      url: `${API_BASE_URL}/api/user/profile-values`,
      contentType: "application/json",
      data: JSON.stringify({ fields }),
      headers: { Authorization: `Bearer ${token}` },
      cache: false,
      crossDomain: true,
    });
    return response && typeof response === "object" ? (response as Record<string, string>) : {};
  } catch (err: any) {
    const status = typeof err?.status === "number" ? err.status : undefined;
    if (status === 403) {
      console.warn("Profilwerte: API verlangt Zustimmung (403). User kann „Zustimmung erteilen“ ausführen.");
      throw new ConsentRequiredError();
    }
    if (status === 401) return {};
    console.warn("Profilwerte nicht geladen (kein Crash):", err?.status, err?.message);
    return {};
  }
}
